import { useState, useEffect, useCallback, useMemo } from 'react'
import NoResults from 'components/NoResults'
import Spinner from 'components/Spinner'
import SearchBar from 'components/SearchBar'
import { isEmpty } from 'lodash'
import ResultTable from './ResultTable'
import { toast } from 'react-toastify'

import { CreateCampaigVoucherModal } from './campaign-voucher-list-actions-modal'
import { postSearchVouchersAPI } from 'api/campaign-voucher'
import { VouchersDataTypes } from 'schemas/campaign-voucher-schemas'
import { VoucherSearchStateStore } from "store/vouchers"
import VoucherListFilterForm, { VoucherFilterFormTypes } from './voucher-list-filter-form'

export const VoucherList = () => {
  const searchArrayString = VoucherSearchStateStore(state => state.searchArrayString)
  const searchArrayStringSet = VoucherSearchStateStore(state => state.searchArrayStringSet)

  const initialVoucherTypeFilter = useMemo(() => {
    if (!searchArrayString) return null
    try {
      const parsed = JSON.parse(searchArrayString)
      return parsed.voucherTypeId ?? null
    } catch {
      return null
    }
  }, [searchArrayString])

  const [voucherTypeFilter, setVoucherTypeFilter] = useState<number | null>(initialVoucherTypeFilter)
  const [postData, setPostData] = useState<VouchersDataTypes[] | null>(null)
  const [loading, setLoading] = useState(false)

  const currentSearchObj = useMemo(() => {
    return searchArrayString ? JSON.parse(searchArrayString) : null
  }, [searchArrayString])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fetchVouchers = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (dataArray: any = {}): Promise<void> => {
      if (!dataArray) {
        setPostData(null)
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const list = await postSearchVouchersAPI({
          voucherId: null,
          voucherCode: dataArray.code || dataArray.voucherCode || null,
          voucherName: dataArray['name/PLU'] || dataArray.voucherName || null,
          productSkus: dataArray.productSKU || null,
          voucherTypeId: dataArray.voucherTypeId ?? null,
          startDate: dataArray.startDate
            ? dataArray.startDate.includes(' ')
              ? dataArray.startDate
              : `${dataArray.startDate} 00:00:00`
            : null,
          pSize: null,
          pNumber: null,
        })
        setPostData(list)

        // Only update store if different
        const newString = JSON.stringify(dataArray)
        if (searchArrayString !== newString) {
          searchArrayStringSet(newString)
        }
      } catch (error) {
        const message = JSON.parse(JSON.stringify(error))
        if (!message.outcomeUserMessage?.includes('Access Token has expired')) {
          toast.error('An unknown error has occured. Please try again')
        }
      } finally {
        setLoading(false)
      }
    },
    [searchArrayString, searchArrayStringSet]
  )

  useEffect(() => {
    if (searchArrayString) {
      fetchVouchers(JSON.parse(searchArrayString))
    }
  }, [fetchVouchers, searchArrayString])

  const handleFilter = (params: VoucherFilterFormTypes) => {
    setVoucherTypeFilter(params.voucherTypeId)
    const base = currentSearchObj ? { ...currentSearchObj } : {}
    const combined = { ...base, ...(params.voucherTypeId != null ? { voucherTypeId: params.voucherTypeId } : {}) }
    fetchVouchers(combined)
  }

  const handleFilterClear = () => {
    setVoucherTypeFilter(null)
    if (currentSearchObj) {
      const { voucherTypeId, ...rest } = currentSearchObj
      if (Object.keys(rest).length > 0) {
        fetchVouchers(rest)
        searchArrayStringSet(JSON.stringify(rest))
      } else {
        setPostData(null)
        searchArrayStringSet('')
      }
    } else {
      setPostData(null)
      searchArrayStringSet('')
    }
  }

  return (
    <div className="p-12 bg-white content-container">
      <div className="flex flex-col lg:flex-row lg:justify-between gap-2">
        <div>
          <h3>
            <span className="tracking-wider font-bold text-xl">Vouchers</span>
          </h3>
          <p>
            <span className="tracking-wider font-light">
              Create and manage vouchers and campaigns.
            </span>
          </p>
          <p className="text-gray-500">
            <span className="tracking-wider font-light text-xs">
              * Use the searchbar to lookup for voucher information
            </span>
          </p>
          <br />
          <p>
            <div className="flex gap-2">
              <CreateCampaigVoucherModal />
            </div>
          </p>
        </div>

        <div className="w-3/4 lg:w-1/2">
          <SearchBar
            placeholder=""
            startFetch={fetchVouchers}
            startLoading={() => setLoading(true)}
            stopLoading={() => setLoading(false)}
            closeSearchForm={() => {
              return
            }}
            isVoucherLookup
          />
        </div>
      </div>

      <div className="my-4">
        <VoucherListFilterForm
          defaultValues={{ voucherTypeId: voucherTypeFilter }}
          onFilter={handleFilter}
          onClear={handleFilterClear}
        />
      </div>

      <div className="my-6">
        {loading ? (
          <Spinner />
        ) : postData == null ? (
          <></>
        ) : isEmpty(postData) ? (
          <NoResults noImage />
        ) : (
          <ResultTable postData={postData} />
        )}
      </div>
    </div>
  )
}
